const ClientBase = require('./client_base');
const { DFundedWrappedWebSocket, buildDFundedAuthorizeStub } = require('./dfunded/dfunded-websocket-wrapper');
const { isDFundedMode } = require('./dfunded/core');
const SocketCache = require('./socket_cache');
const DforgeUsage = require('../dforge_usage');
const State = require('../storage').State;
const cloneObject = require('../utility').cloneObject;
const getPropertyValue = require('../utility').getPropertyValue;
const isEmptyObject = require('../utility').isEmptyObject;
const PromiseClass = require('../utility').PromiseClass;
const getSocketURL = require('../../config').getSocketURL;
const { normalizeV2Response, transformV2Request } = require('../../services/deriv-v2-normalize');

/*
 * An abstraction layer over native javascript WebSocket,
 * which provides additional functionality like
 * reopen the closed connection and process the buffered requests
 */
const BinarySocketBase = (() => {
    let binary_socket;

    let config = {};
    let buffered_sends = [];
    let req_id = 0;
    let is_disconnect_called = false;
    let is_connected_before = false;

    const timeouts = {};
    const promises = {};

    const availability = {
        is_up      : true,
        is_updating: false,
        is_down    : false,
    };

    const no_duplicate_requests = ['authorize', 'asset_index'];

    const sent_requests = {
        items: [],
        clear: () => {
            sent_requests.items = [];
        },
        has: (msg_type) => sent_requests.items.indexOf(msg_type) >= 0,
        add: (msg_type) => {
            if (!sent_requests.has(msg_type)) sent_requests.items.push(msg_type);
        },
        remove: (msg_type) => {
            if (sent_requests.has(msg_type)) {sent_requests.items.splice(sent_requests.items.indexOf(msg_type, 1));}
        },
    };

    const waiting_list = {
        items: {},
        add  : (msg_type, promise_obj) => {
            if (!waiting_list.items[msg_type]) {
                waiting_list.items[msg_type] = [];
            }
            waiting_list.items[msg_type].push(promise_obj);
        },
        resolve: (response) => {
            const msg_type = response.msg_type;
            const this_promises = waiting_list.items[msg_type];
            if (this_promises && this_promises.length) {
                this_promises.forEach((pr) => {
                    if (!waiting_list.another_exists(pr, msg_type)) {
                        pr.resolve(response);
                    }
                });
                waiting_list.items[msg_type] = [];
            }
        },
        another_exists: (pr, msg_type) =>
            Object.keys(waiting_list.items).some(
                (type) =>
                    type !== msg_type && waiting_list.items[type].indexOf(pr) !== -1,
            ),
    };

    const clearTimeouts = () => {
        Object.keys(timeouts).forEach((key) => {
            clearTimeout(timeouts[key]);
            delete timeouts[key];
        });
    };

    const isReady = () => hasReadyState(1);

    const isClose = () => !binary_socket || hasReadyState(2, 3);

    const hasReadyState = (...states) =>
        binary_socket && states.some((s) => binary_socket.readyState === s);

    const sendBufferedRequests = () => {
        while (buffered_sends.length > 0 && !availability.is_down) {
            const req_obj = buffered_sends.shift();
            send(req_obj.request, req_obj.options);
        }
    };

    const wait = (...msg_types) => {
        const promise_obj = new PromiseClass();
        let is_resolved = true;
        msg_types.forEach((msg_type) => {
            const last_response = State.get(['response', msg_type]);
            if (!last_response) {
                if (ClientBase.isLoggedIn()) {
                    waiting_list.add(msg_type, promise_obj);
                    is_resolved = false;
                }
            } else if (msg_types.length === 1) {
                promise_obj.resolve(last_response);
            }
        });
        if (is_resolved) {
            promise_obj.resolve();
        }
        return promise_obj.promise;
    };

    /**
   * @param {Object} data: request object
   * @param {Object} options:
   *      forced  : {boolean}  sends the request regardless the same msg_type has been sent before
   *      msg_type: {string}   specify the type of request call
   *      callback: {function} to call on response of streaming requests
   */
    const send = function (data, options = {}) {
        const promise_obj = options.promise || new PromiseClass();

        if (!data || isEmptyObject(data)) return promise_obj.promise;

        const msg_type =
      options.msg_type || no_duplicate_requests.find((c) => c in data);

        // Fetch from cache
        if (!options.forced) {
            const response = SocketCache.get(data, msg_type);
            if (response) {
                State.set(['response', msg_type], cloneObject(response));
                if (
                    isReady() &&
          !availability.is_down &&
          !options.skip_cache_update &&
          binary_socket.readyState === 1
                ) {
                    // Background cache refresh must go through the same v2
                    // request transform as normal sends — raw v1-shaped
                    // payloads are rejected by the v2 endpoint.
                    binary_socket.send(JSON.stringify(transformV2Request(data)));
                }
                promise_obj.resolve(response);
                return promise_obj.promise;
            }
        }

        // Fetch from state
        if (
            !options.forced &&
      msg_type &&
      no_duplicate_requests.indexOf(msg_type) !== -1
        ) {
            const last_response = State.get(['response', msg_type]);
            if (last_response) {
                promise_obj.resolve(last_response);
                return promise_obj.promise;
            } else if (sent_requests.has(msg_type)) {
                return wait(msg_type).then((response) => {
                    promise_obj.resolve(response);
                    return promise_obj.promise;
                });
            }
        }

        if (!data.req_id) {
            data.req_id = ++req_id;
        }
        promises[data.req_id] = {
            callback: (response) => {
                if (typeof options.callback === 'function') {
                    options.callback(response);
                } else {
                    promise_obj.resolve(response);
                }
            },
            subscribe: !!data.subscribe,
        };

        if (
            isReady() &&
      !availability.is_down &&
      config.isOnline() &&
      binary_socket.readyState === 1
        ) {
            is_disconnect_called = false;
            // Don't add passthrough to time API calls - they should only have req_id and time
            if (
                !getPropertyValue(data, 'passthrough') &&
        !getPropertyValue(data, 'time')
            ) {
                data.passthrough = {};
            }

            const wire_payload = transformV2Request(data);
            binary_socket.send(JSON.stringify(wire_payload));
            config.wsEvent('send');
            if (msg_type && !sent_requests.has(msg_type)) {
                sent_requests.add(msg_type);
            }
        } else if (+data.time !== 1) {
            // Do not buffer all time requests
            buffered_sends.push({
                request: data,
                options: Object.assign(options, { promise: promise_obj }),
            });
        }

        return promise_obj.promise;
    };

    const init = async (options) => {
        if (typeof options === 'object' && config !== options) {
            config = options;
            // Do NOT drop buffered_sends here: requests buffered while the
            // socket was still connecting are plain API payloads, not tied to
            // the old config. Wiping them strands their promises (they never
            // resolve or reject — callers stall until their own timeouts).
            // They flush via sendBufferedRequests() when the socket opens.
            // Page-scoped cleanup on SPA navigation stays in clear().
        }
        clearTimeouts();
        config.wsEvent('init');

        // Remove token from URL immediately during initialization
        // We may still receive token parameter but we don't use it anymore
        const urlParams = new URLSearchParams(window.location.search);
        const tokenParam = urlParams.get('token');
        if (tokenParam) {
            const url = new URL(window.location.href);
            url.searchParams.delete('token');
            window.history.replaceState({}, document.title, url.toString());
        }

        if (isClose() || availability.is_updating) {
            // getSocketURL is async: for v2 it mints a fresh OTP every call (the OTP
            // is one-shot and short-lived, cannot be cached across reconnects).
            let socket_url;
            try {
                socket_url = await getSocketURL();
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error('[BinarySocket] could not resolve socket URL:', err);
                return;
            }
            // A DFunded account trades through DFunded's engine: the wrapped
            // socket answers trading and account requests itself and passes
            // market data to Deriv's public socket (dfunded/ directory).
            binary_socket = isDFundedMode()
                ? new DFundedWrappedWebSocket(socket_url, buildDFundedAuthorizeStub())
                : new WebSocket(socket_url);
            State.set('response', {});
        }

        binary_socket.onopen = () => {
            config.wsEvent('open');

            if (ClientBase.isLoggedIn()) {
                send({ balance: 1, subscribe: 1 }, { forced: true });
            }

            // Send any buffered requests
            sendBufferedRequests();

            if (typeof config.onOpen === 'function') {
                config.onOpen(isReady());
            }

            if (typeof config.onReconnect === 'function' && is_connected_before) {
                config.onReconnect();
            }

            if (!is_connected_before) {
                is_connected_before = true;
            }
        };

        binary_socket.onmessage = (msg) => {
            config.wsEvent('message');
            const raw = msg.data ? JSON.parse(msg.data) : undefined;
            const response = raw && normalizeV2Response(raw);
            if (response) {
                SocketCache.set(response);
                const msg_type = response.msg_type;

                // store in State
                if (
                    !getPropertyValue(response, ['echo_req', 'subscribe']) ||
          /balance/.test(msg_type)
                ) {
                    State.set(['response', msg_type], cloneObject(response));
                }
                // resolve the send promise
                const this_req_id = response.req_id;
                const pr = this_req_id ? promises[this_req_id] : null;
                if (pr && typeof pr.callback === 'function') {
                    pr.callback(response);
                    if (!pr.subscribe) {
                        delete promises[this_req_id];
                    }
                }
                // resolve the wait promise
                waiting_list.resolve(response);

                if (typeof config.onMessage === 'function') {
                    config.onMessage(response);
                }
            }
        };

        binary_socket.onclose = () => {
            sent_requests.clear();
            clearTimeouts();
            config.wsEvent('close');
            DforgeUsage.trackOnce('api_disconnected', 'socket', { authed: !!ClientBase.isLoggedIn() });

            if (typeof config.onDisconnect === 'function' && !is_disconnect_called) {
                config.onDisconnect();
                is_disconnect_called = true;
            }
        };
    };

    const clear = (msg_type) => {
        buffered_sends = [];
        if (msg_type) {
            State.set(['response', msg_type], undefined);
            sent_requests.remove(msg_type);
        }
    };

    const isSiteUp = (status) => /^up$/i.test(status);

    const isSiteUpdating = (status) => /^updating$/i.test(status);

    const isSiteDown = (status) => /^down$/i.test(status);

    // if status is up or updating, consider site available
    // if status is down, consider site unavailable
    const setAvailability = (status) => {
        availability.is_up = isSiteUp(status);
        availability.is_updating = isSiteUpdating(status);
        availability.is_down = isSiteDown(status);
    };

    return {
        init,
        wait,
        send,
        clear,
        clearTimeouts,
        hasReadyState,
        isSiteUpdating,
        isSiteDown,
        setAvailability,
        sendBuffered   : sendBufferedRequests,
        get            : () => binary_socket,
        getAvailability: () => availability,
        setOnDisconnect: (onDisconnect) => {
            config.onDisconnect = onDisconnect;
        },
        setOnReconnect: (onReconnect) => {
            config.onReconnect = onReconnect;
        },
        removeOnReconnect: () => {
            delete config.onReconnect;
        },
        removeOnDisconnect: () => {
            delete config.onDisconnect;
        },
    };
})();

module.exports = BinarySocketBase;
